import { bunja } from 'bunja';
import { createScopeFromContext } from 'bunja/react';
import { Atom, atom } from 'jotai';
import { selectAtom } from 'jotai/utils';
import { createContext } from 'react';

import { OrderTypeEnum } from '@flipster/apis/types';
import { pendingPositionsBunja, type PendingPosition } from '@flipster/r230522/normal-sub/tables/private/orders';
import { openPositionsBunja, type OpenPosition } from '@flipster/r230522/normal-sub/tables/private/positions';
import { atomWithBigNumber } from '@flipster/react/misc/state/atom-with-bignumber';
import Num from '@flipster/utils/num';

import { SymbolScope } from '../symbol/bunja';

export type PositionType = 'open' | 'pending';

export const ParentIdContext = createContext<string>('');
export const ParentIdScope = createScopeFromContext(ParentIdContext);

export const PositionTypeContext = createContext<PositionType>('open');
export const PositionTypeScope = createScopeFromContext(PositionTypeContext);

export const positionTpslBunja = bunja(() => {
  const parentId = bunja.use(ParentIdScope);
  const positionType = bunja.use(PositionTypeScope);
  const symbol = bunja.use(SymbolScope);
  const { openPositionsAtom } = bunja.use(openPositionsBunja);
  const { pendingPositionsAtom } = bunja.use(pendingPositionsBunja);

  const openPositionAtom = atom((get) => {
    if (positionType !== 'open') return undefined;
    return get(openPositionsAtom)?.[symbol]?.find((p) => p.positionId === parentId);
  });

  const pendingPositionAtom = atom((get) => {
    if (positionType !== 'pending') return undefined;
    return get(pendingPositionsAtom)?.[symbol]?.find((p) => p.orderId === parentId);
  });

  // TPSL order atoms
  //
  // Triggered-but-unfilled Limit TP/SL rows (`waitingTrigger === false`) are
  // excluded here — once a Limit TP/SL fires, the row is surfaced on the
  // Open Orders tab instead. Keeping them out of
  // tpOrders/slOrders cascades the right behavior to the manage modal list,
  // the position row's TP/SL button label, and hasTp/tpCount counters.
  //
  // `!== false` is deliberate: pre-feature rows can have `waitingTrigger`
  // unset (`undefined`) and must stay in the TP/SL list, so we only filter
  // out the explicit-`false` triggered case.
  const tpOrdersAtom = atom((get) => {
    switch (positionType) {
      case 'open':
        return (get(openPositionAtom)?.tpOrders ?? []).filter((order) => order.waitingTrigger !== false);
      case 'pending': {
        const pending = get(pendingPositionAtom);
        return pending ? [pending] : [];
      }
    }
  });
  const slOrdersAtom = atom((get) => {
    switch (positionType) {
      case 'open':
        return (get(openPositionAtom)?.slOrders ?? []).filter((order) => order.waitingTrigger !== false);
      case 'pending': {
        const pending = get(pendingPositionAtom);
        return pending ? [pending] : [];
      }
    }
  });

  // 첫 TP/SL 주문 — orderId가 같으면 이전 identity를 유지한다.
  // tpOrdersAtom/slOrdersAtom은 소켓 tick마다 openPositionsAtom이 재생성돼 .filter로 매번 새 배열을
  // 내놓으므로, 배열을 그대로 구독하면 표시값이 안 바뀐 tick에도 리렌더된다. 첫 주문만 필요한 소비처
  // (예: 관리 버튼의 단일 상태 Edit/Remove)는 이 atom을 구독해 값이 실제 바뀐 tick에만 갱신되게 한다.
  const tpFirstOrderAtom = selectAtom(
    tpOrdersAtom,
    (orders) => orders[0],
    (a, b) => a?.orderId === b?.orderId,
  );
  const slFirstOrderAtom = selectAtom(
    slOrdersAtom,
    (orders) => orders[0],
    (a, b) => a?.orderId === b?.orderId,
  );

  // TPSL trigger price atoms
  const tpTriggerPriceAtom = atom((get) => {
    switch (positionType) {
      case 'open':
        return get(tpOrdersAtom)[0]?.triggerPrice ?? null;
      case 'pending':
        return get(pendingPositionAtom)?.orderTpTriggerPrice ?? null;
    }
  });
  const slTriggerPriceAtom = atom((get) => {
    switch (positionType) {
      case 'open':
        return get(slOrdersAtom)[0]?.triggerPrice ?? null;
      case 'pending':
        return get(pendingPositionAtom)?.orderSlTriggerPrice ?? null;
    }
  });

  // TPSL order qty atoms
  const tpOrderQtyAtom = atom((get) => {
    switch (positionType) {
      case 'open':
        return get(tpOrdersAtom)[0]?.qty ?? null;
      case 'pending':
        return get(pendingPositionAtom)?.orderTpOrderQty ?? null;
    }
  });
  const slOrderQtyAtom = atom((get) => {
    switch (positionType) {
      case 'open':
        return get(slOrdersAtom)[0]?.qty ?? null;
      case 'pending':
        return get(pendingPositionAtom)?.orderSlOrderQty ?? null;
    }
  });

  // 공통 파생
  const hasTpAtom = atom((get) => {
    switch (positionType) {
      case 'open':
        return get(tpOrdersAtom).length > 0;
      case 'pending': {
        const price = get(tpTriggerPriceAtom);
        return price != null && !Num(price).isZero();
      }
    }
  });

  const hasSlAtom = atom((get) => {
    switch (positionType) {
      case 'open':
        return get(slOrdersAtom).length > 0;
      case 'pending': {
        const price = get(slTriggerPriceAtom);
        return price != null && !Num(price).isZero();
      }
    }
  });

  const tpCountAtom = atom((get) => {
    switch (positionType) {
      case 'open':
        return get(tpOrdersAtom).length;
      case 'pending':
        return get(hasTpAtom) ? 1 : 0;
    }
  });

  const slCountAtom = atom((get) => {
    switch (positionType) {
      case 'open':
        return get(slOrdersAtom).length;
      case 'pending':
        return get(hasSlAtom) ? 1 : 0;
    }
  });

  const sideAtom = atom((get) => {
    switch (positionType) {
      case 'open':
        return get(openPositionAtom)?.side ?? 'Long';
      case 'pending':
        return get(pendingPositionAtom)?.side ?? 'Long';
    }
  });

  const slotAtom = atom((get) => {
    switch (positionType) {
      case 'open':
        return get(openPositionAtom)?.slot ?? -1;
      case 'pending':
        return get(pendingPositionAtom)?.slot ?? -1;
    }
  });

  // position union
  const positionAtom: Atom<PendingPosition | OpenPosition | undefined> =
    positionType === 'open' ? openPositionAtom : pendingPositionAtom;

  const leverageAtom = atom((get) => get(positionAtom)?.initLeverage || 1);

  // open position derived atoms
  const entryPriceAtom = atomWithBigNumber((get) => Num(get(openPositionAtom)?.avgEntryPrice || '0'));
  const fundsInvestedAtom = atomWithBigNumber((get) => Num(get(openPositionAtom)?.fundsInvested || '0'));
  const isolatedMarginInitialAtom = atomWithBigNumber((get) =>
    Num(get(openPositionAtom)?.isolatedMarginInitial || '0'),
  );
  const isolatedMarginAddedAtom = atomWithBigNumber((get) => Num(get(openPositionAtom)?.isolatedMarginAdded || '0'));
  const markPriceAtom = atomWithBigNumber((get) => Num(get(openPositionAtom)?.markPrice || '0'));

  // pending position derived atoms
  const orderQtyAtom = atomWithBigNumber((get) => Num(get(pendingPositionAtom)?.qty || '0'));
  const orderPriceAtom = atomWithBigNumber((get) => {
    const pendingPosition = get(pendingPositionAtom);
    if (!pendingPosition) return Num(0);
    switch (pendingPosition.orderType) {
      case OrderTypeEnum['Limit']:
        return Num(pendingPosition.price || 0);
      case OrderTypeEnum['StopLimit']:
        // Trigger Limit requires either `price` (fixed limit) or
        // `priceMatchStrategy` (BBO) — they are mutually exclusive per the
        // trade API contract. Use `price` as the entry whenever a fixed
        // limit exists; only the BBO variant lacks a known limit price and
        // must fall back to `triggerPrice` until the matcher stamps it.
        return pendingPosition.priceMatchStrategy
          ? Num(pendingPosition.triggerPrice || 0)
          : Num(pendingPosition.price || 0);
      default:
        return Num(pendingPosition.triggerPrice || 0);
    }
  });

  // position qty (open: abs position size, pending: order qty)
  const positionQtyAtom = atomWithBigNumber((get) => {
    switch (positionType) {
      case 'open':
        return Num(get(openPositionAtom)?.position || '0').abs();
      case 'pending':
        return get(orderQtyAtom);
    }
  });

  const entireTpOrdersAtom = atom((get) => {
    return get(tpOrdersAtom).filter((order) => Num(order.qty || 0).isZero());
  });
  const entireSlOrdersAtom = atom((get) => {
    return get(slOrdersAtom).filter((order) => Num(order.qty || 0).isZero());
  });
  const partialTpOrdersAtom = atom((get) => {
    return get(tpOrdersAtom).filter((order) => !Num(order.qty || 0).isZero());
  });
  const partialSlOrdersAtom = atom((get) => {
    return get(slOrdersAtom).filter((order) => !Num(order.qty || 0).isZero());
  });

  return {
    positionType,
    symbol,
    parentId,
    // position atoms
    positionAtom,
    openPositionAtom,
    pendingPositionAtom,
    // 공통
    sideAtom,
    slotAtom,
    leverageAtom,
    hasTpAtom,
    hasSlAtom,
    tpCountAtom,
    slCountAtom,
    tpOrdersAtom,
    slOrdersAtom,
    tpFirstOrderAtom,
    slFirstOrderAtom,
    // TPSL values
    tpTriggerPriceAtom,
    slTriggerPriceAtom,
    tpOrderQtyAtom,
    slOrderQtyAtom,
    // open position derived
    entryPriceAtom,
    fundsInvestedAtom,
    isolatedMarginInitialAtom,
    isolatedMarginAddedAtom,
    markPriceAtom,
    // pending position derived
    orderQtyAtom,
    orderPriceAtom,
    // position qty
    positionQtyAtom,
    // entire/partial filtered
    entireTpOrdersAtom,
    entireSlOrdersAtom,
    partialTpOrdersAtom,
    partialSlOrdersAtom,
  };
});
positionTpslBunja.debugLabel = 'positionTpslBunja';
